/**
 * Shared utilities for LBS and Takt chart rendering.
 */

/** Ordered location row for Y-axis placement. */
export interface LocationRow {
  id: string;
  name: string;
  locationType: string;
  depth: number;
  y: number;
  hasChildren: boolean;
}

/** A single flowline segment: section/PP at a location over a time range. */
export interface FlowlinePoint {
  locationId: string;
  startDate: Date;
  endDate: Date;
}

/** Complete flowline for one section or procurement package. */
export interface Flowline {
  id: string;
  label: string;
  color: string;
  points: FlowlinePoint[];
}

// Finnish abbreviations for location types
export const LOCATION_TYPE_ABBREV: Record<string, string> = {
  FLOOR: "K",    // Kerros
  ZONE: "A",     // Alue
  SPACE: "T",    // Tila
  BUILDING: "R", // Rakennus
  SECTION: "L",  // Lohko
};

// Location type sort priority (top-level first)
const LOCATION_TYPE_ORDER: Record<string, number> = {
  BUILDING: 0,
  SECTION: 1,
  FLOOR: 2,
  ZONE: 3,
  SPACE: 4,
};

const FLOWLINE_COLORS = [
  "#3b82f6", "#ef4444", "#22c55e", "#f59e0b", "#8b5cf6",
  "#06b6d4", "#ec4899", "#14b8a6", "#f97316", "#6366f1",
];

export function getFlowlineColor(index: number): string {
  return FLOWLINE_COLORS[index % FLOWLINE_COLORS.length];
}

export function dateToPx(
  date: Date,
  startDate: Date,
  endDate: Date,
  chartWidth: number,
): number {
  const totalMs = endDate.getTime() - startDate.getTime();
  if (totalMs <= 0) return 0;
  const offsetMs = date.getTime() - startDate.getTime();
  return (offsetMs / totalMs) * chartWidth;
}

export function parseDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

interface RawLocation {
  id: string;
  name: string;
  locationType: string;
  parentLocationId: string | null;
}

/** Natural sort comparator for location names (handles "1. krs", "2.krs", "A1", "A2", etc.) */
function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, "fi", { numeric: true, sensitivity: "base" });
}

/**
 * Build full location tree in depth-first order, sorted by type then name.
 * Returns ALL rows. Use filterVisibleRows() to apply collapse state.
 */
export function buildLocationRows(
  locations: RawLocation[],
): LocationRow[] {
  const byParent = new Map<string | null, RawLocation[]>();
  for (const loc of locations) {
    const parent = loc.parentLocationId || null;
    if (!byParent.has(parent)) byParent.set(parent, []);
    byParent.get(parent)!.push(loc);
  }

  // Sort children by type priority, then by name naturally
  for (const [, children] of byParent) {
    children.sort((a, b) => {
      const typeA = LOCATION_TYPE_ORDER[a.locationType] ?? 99;
      const typeB = LOCATION_TYPE_ORDER[b.locationType] ?? 99;
      if (typeA !== typeB) return typeA - typeB;
      return naturalCompare(a.name, b.name);
    });
  }

  const rows: LocationRow[] = [];

  function walk(parentId: string | null, depth: number) {
    const children = byParent.get(parentId) || [];
    for (const child of children) {
      const childHasChildren = byParent.has(child.id) && byParent.get(child.id)!.length > 0;
      rows.push({
        id: child.id,
        name: child.name,
        locationType: child.locationType,
        depth,
        y: 0, // computed later after filtering
        hasChildren: childHasChildren,
      });
      walk(child.id, depth + 1);
    }
  }

  walk(null, 0);
  return rows;
}

/**
 * Filter location rows based on which parents are expanded.
 * Returns only visible rows with correct Y positions.
 */
export function filterVisibleRows(
  allRows: LocationRow[],
  expandedIds: Set<string>,
  rowHeight: number,
): LocationRow[] {
  const hiddenIds = new Set<string>();

  // First pass: find all hidden IDs (descendants of collapsed parents)
  for (let i = 0; i < allRows.length; i++) {
    const row = allRows[i];
    if (hiddenIds.has(row.id)) continue;
    if (row.hasChildren && !expandedIds.has(row.id)) {
      // Mark all descendants as hidden
      const parentDepth = row.depth;
      for (let j = i + 1; j < allRows.length; j++) {
        if (allRows[j].depth <= parentDepth) break;
        hiddenIds.add(allRows[j].id);
      }
    }
  }

  // Second pass: collect visible rows with correct Y positions
  const visible: LocationRow[] = [];
  for (const row of allRows) {
    if (hiddenIds.has(row.id)) continue;
    visible.push({
      ...row,
      y: visible.length * rowHeight,
    });
  }

  return visible;
}

/**
 * Get default expanded IDs — expand only top-level (buildings).
 */
export function getDefaultExpandedIds(allRows: LocationRow[]): Set<string> {
  const expanded = new Set<string>();
  for (const row of allRows) {
    if (row.depth === 0 && row.hasChildren) {
      expanded.add(row.id);
    }
  }
  return expanded;
}

/**
 * Build a map from hidden location IDs to their nearest visible ancestor.
 * Used to remap flowline points when locations are collapsed.
 */
export function buildCollapseRemap(
  allRows: LocationRow[],
  expandedIds: Set<string>,
): Map<string, string> {
  const remap = new Map<string, string>();

  for (let i = 0; i < allRows.length; i++) {
    const row = allRows[i];
    if (row.hasChildren && !expandedIds.has(row.id)) {
      // This row is collapsed — map all descendants to this row
      const parentDepth = row.depth;
      for (let j = i + 1; j < allRows.length; j++) {
        if (allRows[j].depth <= parentDepth) break;
        remap.set(allRows[j].id, row.id);
      }
    }
  }

  return remap;
}


